import React, { useEffect, useRef, useState } from 'react';
import { AlertCircle, FileText, Loader2, Maximize2, Minimize2 } from 'lucide-react';
import { apiClient } from '../../utils/api';
import type { ContractStampSignatures } from './types';

export interface TemplatePdfPreviewProps {
  templateId: string | null;
  actId: string | null;
  loading: boolean;
  stampSignatures?: ContractStampSignatures;
}

export const TemplatePdfPreview: React.FC<TemplatePdfPreviewProps> = ({
  templateId,
  actId,
  loading,
  stampSignatures,
}) => {
  const [loadPdf, setLoadPdf] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [retryToken, setRetryToken] = useState(0);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState(false);
  const [pdfErrorDetail, setPdfErrorDetail] = useState<string | null>(null);
  const urlRef = useRef<string | null>(null);

  const stampKey = [
    stampSignatures?.practitioner?.signedAt ?? '',
    stampSignatures?.client?.signedAt ?? '',
    stampSignatures?.legalGuardian?.signedAt ?? '',
  ].join('|');

  useEffect(() => {
    if (!loadPdf || !templateId || !actId) return;
    let cancelled = false;
    setPdfLoading(true);
    setPdfError(false);
    setPdfErrorDetail(null);

    const hasStamps = !!(
      stampSignatures?.practitioner?.dataUrl ||
      stampSignatures?.client?.dataUrl ||
      stampSignatures?.legalGuardian?.dataUrl
    );

    (async () => {
      try {
        let res: Response;
        if (hasStamps) {
          res = await apiClient(`/api/practitioner/acts/${encodeURIComponent(actId)}/contract-display-pdf`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              templateId,
              signatures: {
                ...(stampSignatures?.practitioner
                  ? {
                      practitioner: {
                        dataUrl: stampSignatures.practitioner.dataUrl,
                        signerName: stampSignatures.practitioner.signerName,
                        signedAt: stampSignatures.practitioner.signedAt,
                      },
                    }
                  : {}),
                ...(stampSignatures?.client
                  ? {
                      client: {
                        dataUrl: stampSignatures.client.dataUrl,
                        signerName: stampSignatures.client.signerName,
                        signedAt: stampSignatures.client.signedAt,
                      },
                    }
                  : {}),
                ...(stampSignatures?.legalGuardian
                  ? {
                      legalGuardian: {
                        dataUrl: stampSignatures.legalGuardian.dataUrl,
                        signerName: stampSignatures.legalGuardian.signerName,
                        signedAt: stampSignatures.legalGuardian.signedAt,
                      },
                    }
                  : {}),
              },
            }),
          });
        } else {
          res = await apiClient(
            `/api/practitioner/acts/${encodeURIComponent(actId)}/contract-preview-pdf?templateId=${encodeURIComponent(templateId)}`,
          );
        }
        if (!res.ok) {
          let detail = `HTTP ${res.status}`;
          const ct = res.headers.get('content-type') ?? '';
          if (ct.includes('application/json')) {
            try {
              const j = await res.json();
              if (j && typeof j.message === 'string') detail = j.message;
            } catch {
              /* ignore */
            }
          }
          throw new Error(detail);
        }
        const blob = await res.blob();
        if (cancelled) return;
        if (urlRef.current) URL.revokeObjectURL(urlRef.current);
        const url = URL.createObjectURL(blob);
        urlRef.current = url;
        setBlobUrl(url);
      } catch (e: unknown) {
        if (!cancelled) {
          setPdfError(true);
          setPdfErrorDetail(e instanceof Error ? e.message : null);
        }
      } finally {
        if (!cancelled) setPdfLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
    };
  }, [loadPdf, templateId, actId, loading, stampKey, retryToken]);

  if (!templateId) {
    return (
      <div className="flex items-center justify-center gap-2 text-amber-500 px-4 py-12">
        <AlertCircle className="w-5 h-5" />
        <span className="text-sm">Aucun modèle de contrat défini pour ce type de piercing.</span>
      </div>
    );
  }

  if (!actId) {
    return (
      <div className="flex items-center justify-center gap-2 text-gray-400 px-4 py-12">
        <Loader2 className="w-5 h-5 animate-spin" />
        <span className="text-sm">Préparation du contrat…</span>
      </div>
    );
  }

  if (!loadPdf) {
    return (
      <div className="px-5 py-8 border-t border-gray-100 bg-gray-50/80">
        <div className="max-w-md mx-auto text-center space-y-4">
          <FileText className="w-10 h-10 mx-auto text-gray-400" />
          <p className="text-sm text-gray-600">
            La prévisualisation PDF est générée côté serveur et peut être lourde. Affichez-la lorsque vous en avez besoin,
            puis agrandissez la zone si le document est long.
          </p>
          <button
            type="button"
            className="btn-primary inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold"
            disabled={loading}
            onClick={() => setLoadPdf(true)}
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
            Afficher la prévisualisation PDF
          </button>
        </div>
      </div>
    );
  }

  if (loading || pdfLoading) {
    return (
      <div className="flex items-center justify-center gap-2 text-gray-400 px-4 py-16 border-t border-gray-100">
        <Loader2 className="w-5 h-5 animate-spin" />
        <span className="text-sm">Génération du document…</span>
      </div>
    );
  }

  if (pdfError) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 text-red-500 px-4 py-12 border-t border-gray-100">
        <AlertCircle className="w-5 h-5" />
        <span className="text-sm text-center max-w-md">
          Impossible de charger le document
          {pdfErrorDetail ? (
            <span className="block text-xs text-red-600/90 mt-1 font-normal">{pdfErrorDetail}</span>
          ) : null}
        </span>
        <button
          type="button"
          className="btn-secondary text-sm"
          onClick={() => {
            setPdfError(false);
            setRetryToken((t) => t + 1);
          }}
        >
          Réessayer
        </button>
      </div>
    );
  }

  const iframeHeight = expanded ? 'min(85vh, 960px)' : 'min(40vh, 360px)';

  return (
    <div className="border-t border-gray-100">
      <div
        className={`relative w-full bg-gray-100 ${expanded ? '' : 'overflow-hidden'}`}
        style={{ maxHeight: expanded ? undefined : 'min(40vh, 360px)' }}
      >
        <iframe
          src={blobUrl ?? ''}
          title="Document à signer"
          className="w-full rounded-b-xl bg-white"
          style={{ height: iframeHeight, minHeight: expanded ? 480 : 280 }}
        />
        {!expanded && (
          <div
            className="pointer-events-none absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-gray-200/90 to-transparent"
            aria-hidden
          />
        )}
      </div>
      <div className="flex flex-wrap items-center justify-center gap-2 px-4 py-3 bg-gray-50 border-t border-gray-100">
        <button
          type="button"
          className="btn-secondary text-xs sm:text-sm inline-flex items-center gap-2 px-4 py-2"
          onClick={() => setExpanded((e) => !e)}
        >
          {expanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          {expanded ? 'Réduire la prévisualisation' : 'Voir le document en entier'}
        </button>
      </div>
    </div>
  );
};
